/**
 * balanceSheetChartData.ts – turns the projection engine's per-year rows into the points the
 * projections modal's net-worth and debt charts draw.
 *
 * Both read the same year-end snapshot the engine clones onto every row (`assets`, `home`,
 * `liabilities`) plus the two strategy loans it tracks outside that snapshot
 * (`reverseMortgageLoanBalance`, `heasLoanBalance`). The asset side deliberately mirrors the
 * server's `sumAssetValues`/`calculateNetWorth` (annuities count at their residual value; the home
 * is skipped when an asset row already represents it) so the bars total to the same gross assets
 * the engine netted its `netWorth` from — which is what the dotted line on the net-worth chart is.
 */
import { CHART_CATEGORICAL_SLOTS, type ChartPoint, type ChartSeries, type ChartValueLine } from '../../components/charts/chartTypes.ts';
import { labelsForYear } from './cashflowChartData.ts';

/** A stacked asset category. Ids are stable — the chart's legend toggles key off them. */
export type NetWorthSeriesId = 'home' | 'cash' | 'shares' | 'super' | 'property' | 'rad' | 'other';
/** A stacked debt category. */
export type DebtSeriesId = 'mortgages' | 'reverseMortgage' | 'heas' | 'heloc' | 'personalLoans' | 'creditCards' | 'other';

/** The asset stack, bottom-up, in categorical slot order (see CHART_CATEGORICAL_SLOTS). */
export const NET_WORTH_SERIES: readonly ChartSeries[] = [
    { id: 'home', label: 'Family home', color: CHART_CATEGORICAL_SLOTS[0] },
    { id: 'cash', label: 'Cash & term deposits', color: CHART_CATEGORICAL_SLOTS[1] },
    { id: 'shares', label: 'Shares & managed funds', color: CHART_CATEGORICAL_SLOTS[2] },
    { id: 'super', label: 'Super & pensions', color: CHART_CATEGORICAL_SLOTS[3] },
    { id: 'property', label: 'Investment property', color: CHART_CATEGORICAL_SLOTS[4] },
    { id: 'rad', label: 'RAD paid', color: CHART_CATEGORICAL_SLOTS[5] },
    { id: 'other', label: 'Other assets', color: CHART_CATEGORICAL_SLOTS[6] },
];

/** The debt stack, bottom-up, in the same slot order. */
export const DEBT_SERIES: readonly ChartSeries[] = [
    { id: 'mortgages', label: 'Mortgage / home loan', color: CHART_CATEGORICAL_SLOTS[0] },
    { id: 'reverseMortgage', label: 'Reverse mortgage', color: CHART_CATEGORICAL_SLOTS[1] },
    { id: 'heas', label: 'Home Equity Access Scheme', color: CHART_CATEGORICAL_SLOTS[2] },
    { id: 'heloc', label: 'Loan against the home', color: CHART_CATEGORICAL_SLOTS[3] },
    { id: 'personalLoans', label: 'Personal loans', color: CHART_CATEGORICAL_SLOTS[4] },
    { id: 'creditCards', label: 'Credit cards', color: CHART_CATEGORICAL_SLOTS[5] },
    { id: 'other', label: 'Other debt', color: CHART_CATEGORICAL_SLOTS[6] },
];

interface AssetLike {
    type?: string;
    value?: number;
    balance?: number;
    residualValue?: number;
    description?: string;
    name?: string;
    [key: string]: unknown;
}

interface LiabilityLike {
    type?: string;
    balance?: number;
    description?: string;
    [key: string]: unknown;
}

/** The subset of a projection row these builders read. Structural, so the modal's richer type fits. */
export interface BalanceSheetRowLike {
    fyLabel?: string;
    netWorth?: number;
    home?: { value?: number } | null;
    assets?: Record<string, AssetLike[] | undefined> | null;
    liabilities?: Record<string, LiabilityLike[] | undefined> | null;
    /** Strategy loans the engine tracks outside `liabilities` — see ProjectionYearResult. */
    reverseMortgageLoanBalance?: number;
    heasLoanBalance?: number;
    [key: string]: unknown;
}

const EMPTY_ASSETS = (): Record<NetWorthSeriesId, number> => ({ home: 0, cash: 0, shares: 0, super: 0, property: 0, rad: 0, other: 0 });
const EMPTY_DEBTS = (): Record<DebtSeriesId, number> => ({ mortgages: 0, reverseMortgage: 0, heas: 0, heloc: 0, personalLoans: 0, creditCards: 0, other: 0 });

/** Route an asset bucket (the fact-find's `assets` keys, plus the engine's synthesized `radPaid`) to a series. */
function seriesForAssetBucket(bucket: string): NetWorthSeriesId {
    switch (bucket) {
        case 'bankAccounts':
        case 'termDeposits':
            return 'cash';
        case 'shares':
        case 'investmentBonds':
            return 'shares';
        case 'superannuation':
        case 'accountBasedPensions':
            return 'super';
        case 'investmentProperty':
            return 'property';
        case 'radPaid':
            return 'rad';
        case 'home':
            return 'home';
        // annuities, funeralBonds, personalAssets, other, and anything a strategy injects.
        default:
            return 'other';
    }
}

/** Route a liability bucket to a series. A row's own `type` wins — strategies inject typed rows into generic buckets. */
function seriesForLiability(bucket: string, liability: LiabilityLike): DebtSeriesId {
    if (liability.type === 'reverse_mortgage') return 'reverseMortgage';
    switch (bucket) {
        case 'mortgages':
            return 'mortgages';
        case 'personalLoans':
            return 'personalLoans';
        case 'creditCards':
            return 'creditCards';
        case 'helocDebt':
            return 'heloc';
        default:
            return 'other';
    }
}

/** Mirrors the server's `sumAssetValues`: annuities count at their residual value, everything else at `value`. */
function assetValue(asset: AssetLike): number {
    const raw = asset.type === 'annuities' && asset.residualValue !== undefined ? asset.residualValue : asset.value;
    return Number.isFinite(raw) ? (raw as number) : 0;
}

/** Every asset row on a year, bucket by bucket. */
function forEachAsset(row: BalanceSheetRowLike, visit: (bucket: string, asset: AssetLike) => void): void {
    Object.entries(row.assets || {}).forEach(([bucket, list]) => (list || []).forEach((asset) => asset && visit(bucket, asset)));
}

/** Every liability row on a year, bucket by bucket. */
function forEachLiability(row: BalanceSheetRowLike, visit: (bucket: string, liability: LiabilityLike) => void): void {
    Object.entries(row.liabilities || {}).forEach(([bucket, list]) => (list || []).forEach((liability) => liability && visit(bucket, liability)));
}

/** A year's debt lines for the tooltip, with the strategy loans appended. Only non-zero balances. */
function debtLinesFor(row: BalanceSheetRowLike, labelFor: (bucket: string, liability: LiabilityLike) => string): ChartValueLine[] {
    const lines: ChartValueLine[] = [];
    forEachLiability(row, (bucket, liability) => {
        const balance = liability.balance || 0;
        if (balance > 0) lines.push({ label: labelFor(bucket, liability), value: balance });
    });
    if ((row.reverseMortgageLoanBalance || 0) > 0) lines.push({ label: 'Reverse mortgage', value: row.reverseMortgageLoanBalance as number });
    if ((row.heasLoanBalance || 0) > 0) lines.push({ label: 'Home Equity Access Scheme', value: row.heasLoanBalance as number });
    return lines;
}

/** The tooltip name for a liability row: its own description, else the series it lands in. */
function liabilityLabel(bucket: string, liability: LiabilityLike): string {
    const described = (liability.description || '').trim();
    if (described) return described;
    const seriesId = seriesForLiability(bucket, liability);
    return DEBT_SERIES.find((series) => series.id === seriesId)?.label || 'Debt';
}

/**
 * Build the net-worth chart's per-year points: gross assets stacked by category. Net Worth itself —
 * the engine's own figure, after every liability — is the tooltip's summary rather than a level
 * drawn across the plot: it always sits under the bars it was netted from, so a line there reads as
 * a second, unexplained quantity rather than as the total the reader came for.
 */
export function buildNetWorthChartYears(projections: BalanceSheetRowLike[]): ChartPoint[] {
    return (projections || []).map((year, index) => {
        const values = EMPTY_ASSETS();
        // The home is a fact-find field, not an asset row — except where a strategy has injected it
        // as one, in which case counting `home.value` too would double it (mirrors calculateNetWorth).
        let homeHeldAsAsset = false;
        forEachAsset(year, (bucket, asset) => {
            if (asset.type === 'home') homeHeldAsAsset = true;
            values[seriesForAssetBucket(asset.type === 'home' ? 'home' : bucket)] += assetValue(asset);
        });
        if (!homeHeldAsAsset) values.home += year.home?.value || 0;

        const netWorth = year.netWorth || 0;
        const debtLines = debtLinesFor(year, liabilityLabel);
        const totalDebt = debtLines.reduce((sum, line) => sum + line.value, 0);

        return {
            ...labelsForYear(year, index),
            values,
            sections: totalDebt > 0 ? [{ title: 'Less debt', lines: debtLines, total: { label: 'Total debt', value: totalDebt } }] : [],
            summary: { label: 'Net Worth', value: netWorth, tone: netWorth >= 0 ? 'positive' : 'negative' },
        };
    });
}

/**
 * Build the debt chart's per-year points: what is owed, stacked by category. No reference level —
 * debt is read against zero, and the goal is to see the stack shrink (or not).
 */
export function buildDebtChartYears(projections: BalanceSheetRowLike[]): ChartPoint[] {
    return (projections || []).map((year, index) => {
        const values = EMPTY_DEBTS();
        forEachLiability(year, (bucket, liability) => {
            values[seriesForLiability(bucket, liability)] += liability.balance || 0;
        });
        values.reverseMortgage += year.reverseMortgageLoanBalance || 0;
        values.heas += year.heasLoanBalance || 0;

        const lines = debtLinesFor(year, liabilityLabel);
        const total = lines.reduce((sum, line) => sum + line.value, 0);

        return {
            ...labelsForYear(year, index),
            values,
            // The stack breakdown already names every category; this section names the individual
            // loans within them, which is the only thing the bars can't show.
            sections: lines.length > 1 ? [{ title: 'Each debt', lines, total: { label: 'Total debt', value: total } }] : [],
        };
    });
}
